import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import axios from "axios";
import Cookies from "js-cookie";
import DatePickerComponent from "../datepicker/DatePicker";
import '../../styles/analytics/analytics.css';
import ExportAnalyticsButton from '../buttons/ExportToExcel';

// Modal showing a single sector/site's approved records broken down by day
const SiteDailyRecordsModal = ({ isOpen, onClose, siteData, activeTab, startDate, endDate }) => {
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (isOpen && siteData) {
      fetchDailyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, siteData, startDate, endDate]);

  const fetchDailyData = async () => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = activeTab === "scanners"
        ? `${baseUrl}/api/sites/${siteData.id}/daily-analytics`
        : `${baseUrl}/api/index/sites/${siteData.id}/daily-analytics`;

      const params = {};
      if (startDate && endDate) {
        params.startDate = startDate.toISOString().split("T")[0];
        params.endDate = endDate.toISOString().split("T")[0];
      }

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${Cookies.get("token")}`,
        },
        params,
      });

      setDailyData(response.data || []);
    } catch (error) {
      setError(`Failed to load daily records for ${siteData.name}`);
      setDailyData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit'
    });
  };

  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  if (!isOpen) return null;

  const xAxisData = dailyData.map(item => formatDate(item.date));
  const series = [
    {
      name: 'Acts',
      type: 'bar',
      stack: 'records',
      data: dailyData.map(item => item.acts ?? 0),
      itemStyle: { color: '#078ECE' },
      barWidth: 20,
    },
    {
      name: 'Register',
      type: 'bar',
      stack: 'records',
      data: dailyData.map(item => item.register ?? 0),
      itemStyle: { color: '#F5A623' },
      barWidth: 20,
    },
    {
      name: 'Yellow Forms',
      type: 'bar',
      stack: 'records',
      data: dailyData.map(item => item.yellowForms ?? 0),
      itemStyle: { color: '#10B981', barBorderRadius: [6, 6, 0, 0] },
      barWidth: 20,
    }
  ];

  const modalOption = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#fff',
      borderColor: '#eee',
      borderWidth: 1,
      textStyle: { color: '#25282C', fontFamily: 'Jost, sans-serif' },
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-radius: 8px;',
      axisPointer: { type: 'shadow' },
      formatter: function(params) {
        const dataIndex = params[0].dataIndex;
        const date = xAxisData[dataIndex];
        const dayOfWeek = getDayOfWeek(dailyData[dataIndex]?.date);
        let tooltip = `<b>${dayOfWeek}, ${date}</b><br/>`;
        params.forEach(item => {
          tooltip += `${item.marker} ${item.seriesName}: ${item.value}<br/>`;
        });
        return tooltip;
      }
    },
    legend: {
      data: ['Acts', 'Register', 'Yellow Forms'],
      bottom: 0,
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: '#25282C', fontSize: 12, fontFamily: 'Jost, sans-serif' },
      align: 'auto',
    },
    grid: {
      left: 40,
      right: 40,
      top: 40,
      bottom: 60,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: xAxisData,
      axisLabel: {
        fontSize: 12,
        color: '#25282C80',
        fontFamily: 'Jost, sans-serif',
        interval: 0,
        rotate: 45
      },
      axisLine: { show: true, lineStyle: { type: 'solid', color: '#25282C80', width: 1 } },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 12, color: '#25282C80', fontFamily: 'Jost, sans-serif' },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: '#E5E7EB', type: 'dashed' } }
    },
    series: series,
    barCategoryGap: '20%'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        width: '800px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid #E5E7EB'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '18px',
            fontWeight: 600,
            color: '#222'
          }}>
            Daily Records - {siteData?.name}
            {startDate && endDate && (
              <span style={{
                fontSize: '14px',
                fontWeight: 400,
                color: '#666',
                marginLeft: '8px'
              }}>
                ({startDate.toLocaleDateString()}
                {startDate.toDateString() !== endDate.toDateString() && ` - ${endDate.toLocaleDateString()}`})
              </span>
            )}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <div style={{ height: '400px', overflowY: 'auto' }}>
          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: '#666'
            }}>
              Loading daily records...
            </div>
          ) : error ? (
            <div style={{
              textAlign: 'center',
              color: '#ef4444',
              padding: '40px'
            }}>
              {error}
            </div>
          ) : dailyData.length === 0 ? (
            <div style={{
              textAlign: 'center',
              color: '#6C6C6C',
              padding: '40px'
            }}>
              No daily records available for this sector
              {startDate && endDate && ` between ${startDate.toLocaleDateString()} and ${endDate.toLocaleDateString()}`}.
            </div>
          ) : (
            <ReactECharts
              option={modalOption}
              style={{ height: '100%', width: '100%' }}
              notMerge={true}
              lazyUpdate={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const ScannedRecordsChart = ({ activeTab = "scanners" }) => {
  const [chartData, setChartData] = useState([]);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(new Date("2024-01-01"));
  const [endDate, setEndDate] = useState(new Date());
  const [dateRangeTouched, setDateRangeTouched] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);

  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Dates are shown in the pickers by default but only applied as a filter
  // once the user actually changes one of them.
  const effectiveStartDate = dateRangeTouched ? startDate : null;
  const effectiveEndDate = dateRangeTouched ? endDate : null;

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setDateRangeTouched(true);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setDateRangeTouched(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoint = activeTab === "scanners"
          ? `${baseUrl}/api/sites/active/approved/analytics`
          : `${baseUrl}/api/index/sites/analytics`;

        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          params: {
            startDate: effectiveStartDate ? effectiveStartDate.toISOString().split("T")[0] : undefined,
            endDate: effectiveEndDate ? effectiveEndDate.toISOString().split("T")[0] : undefined,
          },
        });

        const data = response.data?.map((item) => ({
          id: item.id,
          name: item.sector || item.district,
          acts: item.actsCount ?? 0,
          register: item.registerCount ?? 0,
          yellowForms: item.yellowFormsCount ?? 0,
        }));
        setChartData(data);
        setError(null);
      } catch (error) {
        setError(`Failed to load ${activeTab === "scanners" ? "scanned records" : "indexer"} data`);
        setChartData([]);
      }
    };
    fetchData();
  }, [effectiveStartDate, effectiveEndDate, activeTab, baseUrl]);

  const handleBarClick = (params) => {
    const clickedSite = chartData[params.dataIndex];
    setSelectedSite(clickedSite);
    setModalOpen(true);
  };

  const names = chartData.map(item => item.name);
  const acts = chartData.map(item => item.acts);
  const register = chartData.map(item => item.register);
  const yellowForms = chartData.map(item => item.yellowForms);

  const series = [
    {
      name: 'Acts',
      type: 'bar',
      stack: 'records',
      data: acts,
      itemStyle: { color: '#078ECE' },
      barWidth: 20,
    },
    {
      name: 'Register',
      type: 'bar',
      stack: 'records',
      data: register,
      itemStyle: { color: '#F5A623' },
      barWidth: 20,
    },
    {
      name: 'Yellow Forms',
      type: 'bar',
      stack: 'records',
      data: yellowForms,
      itemStyle: { color: '#10B981', barBorderRadius: [6, 6, 0, 0] },
      barWidth: 20,
    }
  ];

  const option = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#fff',
      borderColor: '#eee',
      borderWidth: 1,
      textStyle: {color: '#25282C', fontFamily: 'Jost, sans-serif' },
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-radius: 8px;',
      axisPointer: { type: 'shadow' },
      formatter: function(params) {
        const dataIndex = params[0].dataIndex;
        const name = names[dataIndex];
        let tooltip = `<b>${name}</b><br/>`;
        params.forEach(item => {
          tooltip += `${item.marker} ${item.seriesName}: ${item.value}<br/>`;
        });
        tooltip += '<br/><i style="color: #666; font-size: 12px;">Click to view daily records</i>';
        return tooltip;
      }
    },
    legend: {
      data: ['Acts', 'Register', 'Yellow Forms'],
      bottom: 0,
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      textStyle: { color: '#25282C', fontSize: 14, fontFamily: 'Jost, sans-serif' },
      align: 'auto',
    },
    grid: {
      left: 40,
      right: 40,
      top: 60,
      bottom: 60,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: names,
      axisLabel: { 
        fontSize: 14, 
        color: '#25282C80', 
        fontFamily: 'Jost, sans-serif',
        interval: 0,
        rotate: 45 // Rotate labels by 45 degrees
      },
      axisLine: { show: true, lineStyle: { type: 'solid', color: '#25282C80', width: 1 } },
      axisTick: { show: false },
      splitLine: { show: false }
    },
    yAxis: {
      type: 'value',
      axisLabel: { fontSize: 14, color: '#25282C80', fontFamily: 'Jost, sans-serif',  },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: '#E5E7EB', type: 'dashed' } }
    },
    series: series,
    barCategoryGap: '25%'
  };

  const getTitle = () => {
    return activeTab === "scanners" ? "Scanned Records" : "Indexed Records";
  };

  return (
    <div className="analytics-container">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, marginTop: 8 }}>
        <h2 style={{ fontWeight: 600, fontSize: 20, color: "#222", margin: 0 }}>
          {getTitle()}
        </h2>
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          <ExportAnalyticsButton
            activeTab={activeTab}
            startDate={effectiveStartDate}
            endDate={effectiveEndDate}
            endpoint="/api/sites/analytics/export-excel"
            typeParam="siteType"
            filePrefix={activeTab === "scanners" ? "scanned_records_by_sector" : "indexed_records_by_sector"}
          />
          <div className="date-picker-container" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <DatePickerComponent selectedDate={startDate} onDateChange={handleStartDateChange} search={true} />
            <span style={{ color: "#666", fontSize: 14 }}>to</span>
            <DatePickerComponent selectedDate={endDate} onDateChange={handleEndDateChange} isEndDate={true} startDate={startDate} />
          </div>
        </div>
      </div>

      {chartData.length > 0 && (
        <p style={{
          fontSize: '14px',
          color: '#666',
          marginBottom: '16px',
          fontStyle: 'italic'
        }}>
          Click on any bar to view daily records for that sector
        </p>
      )}

      {chartData.length === 0 ? (
        <p style={{ textAlign: "center", color: "#6C6C6C", marginBlock: "100px" }}>
          No data available for the selected date range.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <ReactECharts
            option={option}
            className="analytics-chart"
            style={{ height: 400, width: Math.max(600, names.length * 45) }}
            notMerge={true}
            lazyUpdate={true}
            onEvents={{
              'click': handleBarClick
            }}
          />
        </div>
      )}
      {error && (
        <p style={{ textAlign: "center", color: "red" }}>{error}</p>
      )}

      <SiteDailyRecordsModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        siteData={selectedSite}
        activeTab={activeTab}
        startDate={effectiveStartDate}
        endDate={effectiveEndDate}
      />
    </div>
  );
};

export default ScannedRecordsChart;